import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { isReservedLabelName } from '@ipfabric/utils/labels';
import { useEffect } from 'react';
import { type SubmitHandler, useWatch } from 'react-hook-form';
import { useNotifications } from 'utils/hooks/useNotifications';
import { LabelValuesEditor } from './LabelValuesEditor';
import { ReservedLabelNameSuggestions } from './ReservedLabelNameSuggestions';
import {
  type EditableLabel,
  createLabel,
  updateLabel,
} from './labelCatalogApi';
import {
  type LabelFormSubmitValues,
  type LabelFormValues,
  getLabelValuesFieldError,
  labelFormSchema,
} from './labelFormSchema';

type LabelFormAddProps = {
  formType: 'add';
  onClose: () => void;
  onSave: () => void;
};

type LabelFormEditProps = {
  formType: 'edit';
  label: EditableLabel;
  onClose: () => void;
  onSave: () => void;
};

type LabelFormProps = LabelFormAddProps | LabelFormEditProps;

export const LabelForm = (props: LabelFormProps) => {
  const { formType, onClose, onSave } = props;
  const label = formType === 'edit' ? props.label : undefined;
  const { showNotification } = useNotifications();
  const form = useCustomForm<LabelFormValues, unknown, LabelFormSubmitValues>({
    defaultValues: {
      name: label?.name ?? '',
      isNoValue: label ? label.values.length === 0 : false,
      values: label?.values ?? [],
    },
    resolver: zodResolver(labelFormSchema),
  });

  const { control, formState, handleSubmit, resetField, setValue } = form;
  const { dirtyFields, errors, isSubmitting } = formState;
  void formState.isDirty;
  const isNoValue = useWatch({ control, name: 'isNoValue' }) ?? false;
  const values = useWatch({ control, name: 'values' }) ?? [];
  const labelName = useWatch({ control, name: 'name' }) ?? '';

  useEffect(() => {
    if (isNoValue && values.length > 0) {
      setValue('values', [], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [isNoValue, setValue, values.length]);

  const handleSaveClick = () => {
    if (formType === 'edit' && !form.formState.isDirty) {
      onClose();
      return;
    }

    void handleSubmit(onSubmit)();
  };

  const onSubmit: SubmitHandler<LabelFormSubmitValues> = async (data) => {
    if (!form.formState.isDirty) {
      return;
    }

    const body = {
      name: data.name,
      values: data.values,
    };

    if (formType === 'add') {
      await createLabel(body);
    } else {
      await updateLabel(props.label.id, body);
    }

    showNotification({
      type: 'success',
      message: 'Label saved',
    });
    onSave();
  };

  return (
    <SidePanel
      title={formType === 'add' ? 'New label' : 'Edit label'}
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            className='flex-1'
            variant='primary'
            type='submit'
            icon='fa-floppy-disk'
            loading={isSubmitting}
            onClick={handleSaveClick}
            dataTestId='label-form-submit'
          >
            {formType === 'add' ? 'Add label' : 'Update label'}
          </Button>
          <Button className='flex-1' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <form>
        <Card className='flex flex-col gap p-sm'>
          <FormTextField<LabelFormValues>
            control={control}
            dataTestId='label-form-name'
            disabled={label !== undefined && isReservedLabelName(label.name)}
            label='Label name'
            name='name'
            onClear={
              dirtyFields.name
                ? () => {
                    resetField('name');
                  }
                : undefined
            }
            rules={{ required: true }}
            type='text'
          />
          {formType === 'add' ? (
            <ReservedLabelNameSuggestions
              onSelect={(name) =>
                setValue('name', name, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
          ) : null}
          <div data-testid='label-form-no-value'>
            <FormSwitch<LabelFormValues>
              control={control}
              description='Create as a "no-value" label'
              helperText='Use this to create a simple tag-like label without specified value(s).'
              name='isNoValue'
            />
          </div>
          <LabelValuesEditor
            disabled={isNoValue}
            error={getLabelValuesFieldError(errors.values)}
            labelName={labelName.trim()}
            onChange={(updatedValues) =>
              setValue('values', updatedValues, {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              })
            }
            values={values}
          />
        </Card>
      </form>
    </SidePanel>
  );
};
